import {
  Role,
  UserCreateFormData,
  UserUpdateFormData,
} from '@/types/api-responses';

// Common validation rules
export const userValidationRules = {
  email: {
    required: 'Email address is required',
    pattern: {
      value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      message: 'Please enter a valid email address',
    },
    maxLength: {
      value: 100,
      message: 'Email address cannot exceed 100 characters',
    },
  },
  username: {
    required: 'Username is required',
    minLength: {
      value: 3,
      message: 'Username must be at least 3 characters long',
    },
    maxLength: {
      value: 30,
      message: 'Username cannot exceed 30 characters',
    },
    pattern: {
      value: /^[a-zA-Z0-9_-]+$/,
      message:
        'Username can only contain letters, numbers, underscores, and hyphens',
    },
  },
  fullname: {
    maxLength: {
      value: 100,
      message: 'Full name cannot exceed 100 characters',
    },
    pattern: {
      value: /^[a-zA-Z\s'-]+$/,
      message:
        'Full name can only contain letters, spaces, apostrophes, and hyphens',
    },
  },
  phone: {
    pattern: {
      value: /^[+]?\d{1,15}$/,
      message: 'Please enter a valid phone number',
    },
  },
  roleId: {
    required: 'Please select a role for this user',
  },
};

// Helper: generate username from full name
export const generateUsernameFromFullName = (fullName: string): string => {
  if (!fullName) return '';
  const normalized = fullName
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_{2,}/g, '_');
  return normalized || '';
};

// Transform roles for select options
export const transformRolesForSelect = (roles: Role[]) => {
  return roles.map(role => ({
    value: role._id,
    label: role.name.charAt(0).toUpperCase() + role.name.slice(1),
    description: `${role.permissions?.length || 0} permissions`,
    disabled: !role.isActive,
  }));
};

// Default form values
export const getDefaultUserCreateValues = (): UserCreateFormData => ({
  email: '',
  username: '',
  fullname: '',
  roleId: '',
});

export const getDefaultUserUpdateValues = (user?: any): UserUpdateFormData => ({
  email: user?.email || '',
  username: user?.username || '',
  fullname: user?.fullname || '',
  phone: user?.phone || '',
  roleId: '',
  isActive: user?.isActive ?? true,
});

// Helper to get role ID from user data
export const getUserRoleId = (user: any, roles: Role[]): string => {
  if (!user?.role) return '';

  // If user.role is an object with _id
  if (typeof user.role === 'object' && user.role._id) {
    return user.role._id;
  }

  // If user.role is a string (could be _id or name)
  if (typeof user.role === 'string') {
    // First try to find by _id
    const roleById = roles.find(role => role._id === user.role);
    if (roleById) return user.role;

    // Then try to find by name and return _id
    const roleByName = roles.find(role => role.name === user.role);
    if (roleByName) return roleByName._id;
  }

  return '';
};

// Form submission helpers
export const prepareCreateUserPayload = (formData: UserCreateFormData) => ({
  email: formData.email.trim().toLowerCase(),
  username: (
    formData.username || generateUsernameFromFullName(formData.fullname || '')
  ).trim(),
  fullname: formData.fullname?.trim() || undefined,
  roleId: formData.roleId, // API expects 'roleId' for user invite
});

export const prepareUpdateUserPayload = (formData: UserUpdateFormData) => {
  const derivedUsername = generateUsernameFromFullName(formData.fullname || '');
  return {
    email: formData.email.trim().toLowerCase(),
    username: (derivedUsername || formData.username || '').trim(),
    fullname: formData.fullname?.trim() || undefined,
    phone: formData.phone?.trim() || undefined,
    role: formData.roleId, // API expects 'role' not 'roleId' for user updates
    isActive: formData.isActive,
  };
};

// Error message helpers
export const getApiErrorMessage = (
  error: any,
  defaultMessage: string
): string => {
  // Handle validation errors
  if (error?.errors && Array.isArray(error.errors)) {
    return error.errors.map((err: any) => err.message).join(', ');
  }

  // Handle single error message
  if (error?.message) {
    return error.message;
  }

  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }

  return defaultMessage;
};

// Loading state helpers
export const getLoadingStates = (
  isSubmitting: boolean,
  isLoadingRoles: boolean,
  isDeleting?: boolean
) => ({
  isFormDisabled: isSubmitting || isLoadingRoles,
  isSubmitDisabled: isSubmitting,
  isDeleteDisabled: isDeleting || false,
  showLoadingSpinner: isSubmitting || isLoadingRoles || isDeleting || false,
});

// Form field state helpers
export const getFieldProps = (
  field: any,
  fieldState: any,
  isDisabled: boolean = false
) => ({
  value: field.value || '',
  onChange: field.onChange,
  onBlur: field.onBlur,
  error: fieldState?.error?.message,
  disabled: isDisabled,
});

// Success messages
export const getSuccessMessages = {
  userCreated: (displayName: string) =>
    `User "${displayName}" has been created and invited successfully. They will receive an email to set their password.`,
  userUpdated: (displayName: string) =>
    `User "${displayName}" has been updated successfully.`,
  userDeleted: (displayName: string) =>
    `User "${displayName}" has been deleted successfully.`,
};

// Accessibility helpers
export const getAccessibilityProps = (fieldId: string, hasError: boolean) => ({
  id: fieldId,
  'aria-invalid': hasError,
  'aria-describedby': hasError ? `${fieldId}-error` : undefined,
});
